/**
 * Admin Guard
 *
 * RBAC middleware that validates ADMIN role from JWT.
 * Blueprint Task: 2.3.2
 */

import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { useAuth } from '../providers/AuthProvider';

interface AdminGuardProps {
  children: ReactNode;
  /** Required role (defaults to 'admin') */
  requiredRole?: 'owner' | 'admin' | 'super_admin' | 'operator';
}

/**
 * Logs access attempts for audit purposes
 */
function logAccessAttempt(
  userId: string | undefined,
  path: string,
  allowed: boolean,
  role: string | undefined
): void {
  const timestamp = new Date().toISOString();
  const logEntry = {
    timestamp,
    event: 'admin_access_attempt',
    userId: userId ?? 'anonymous',
    path,
    allowed,
    userRole: role ?? 'none',
  };

  // In production, this would send to CloudWatch/audit service
  if (!allowed) {
    console.warn('[AdminGuard] Access denied:', logEntry);
  } else {
    console.info('[AdminGuard] Access granted:', logEntry);
  }
}

/**
 *
 */
export function AdminGuard({ children, requiredRole = 'admin' }: AdminGuardProps) {
  const { user, isLoading, isAuthenticated } = useAuth();
  const location = useLocation();

  // Get user's role from user object
  const userRole = user?.role;

  // Check if user has required admin role
  // Owner has highest precedence and grants all admin access
  const hasAdminRole =
    userRole === 'owner' ||
    userRole === 'admin' ||
    userRole === 'super_admin' ||
    userRole === 'operator';

  // Check if user has the specific required role
  // Role hierarchy: owner > admin > super_admin > operator
  // Higher roles can access lower role areas
  const hasRequiredRole =
    requiredRole === 'admin'
      ? hasAdminRole // Any admin-level role (owner, admin, super_admin, operator)
      : requiredRole === 'owner'
        ? userRole === 'owner' // Only owner
        : requiredRole === 'super_admin'
          ? userRole === 'owner' || userRole === 'super_admin' // Owner can access super_admin areas
          : requiredRole === 'operator'
            ? userRole === 'owner' || userRole === 'super_admin' || userRole === 'operator'
            : false;

  // Log access attempt when auth state resolves
  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      logAccessAttempt(user?.userId, location.pathname, hasRequiredRole, userRole);
    }
  }, [isLoading, isAuthenticated, user?.userId, location.pathname, hasRequiredRole, userRole]);

  // Show loading state
  if (isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: 'hsl(var(--color-neutral-900))' }}
      >
        <LoadingSpinner size="lg" label="Verifying access..." />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Show 403 if user doesn't have required role
  if (!hasRequiredRole) {
    return (
      <div
        className="flex min-h-screen items-center justify-center"
        style={{ backgroundColor: 'hsl(var(--color-neutral-900))' }}
      >
        <div className="text-center">
          <div
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
            style={{ backgroundColor: 'hsl(var(--color-error-900))' }}
          >
            <svg
              className="h-8 w-8"
              style={{ color: 'hsl(var(--color-error-400))' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h1
            className="mt-6 text-2xl font-bold"
            style={{ color: 'hsl(var(--color-neutral-100))' }}
          >
            Access Denied
          </h1>
          <p className="mt-2 text-sm" style={{ color: 'hsl(var(--color-neutral-400))' }}>
            You don't have permission to access this area.
          </p>
          <p className="mt-1 text-xs" style={{ color: 'hsl(var(--color-neutral-500))' }}>
            Error 403 - Forbidden
          </p>
          <div className="mt-6">
            <a
              href="/dashboard"
              className="inline-block rounded-md px-4 py-2 text-sm font-medium"
              style={{
                backgroundColor: 'hsl(var(--color-neutral-800))',
                color: 'hsl(var(--color-neutral-100))',
              }}
            >
              Return to Dashboard
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
